"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ContractManager = void 0;
const RewardPool_json_1 = __importDefault(require("./abi/RewardPool.json"));
const IERC20_json_1 = __importDefault(require("./abi/IERC20.json"));
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const bs58_1 = __importDefault(require("bs58"));
class ContractManager {
    constructor(keyProvider, config) {
        this.keyProvider = keyProvider;
        this.rewardPool = keyProvider.createContract(RewardPool_json_1.default, config.rewardPoolAddress);
    }
    getRewardPoolBalance(account) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.keyProvider.getTokenBalance(this.rewardPool, account);
        });
    }
    getStakingTokenBalance(account) {
        return __awaiter(this, void 0, void 0, function* () {
            const rewardToken = yield this.rewardPool.methods
                .getStakingToken()
                .call();
            const rewardTokenContract = this.keyProvider.createContract(IERC20_json_1.default, rewardToken);
            return this.keyProvider.getTokenBalance(rewardTokenContract, account);
        });
    }
    getRewardPayouts() {
        return __awaiter(this, void 0, void 0, function* () {
            const rawRewardPayouts = yield this.rewardPool.methods
                .getRewardPayouts()
                .call();
            return rawRewardPayouts.map((payout) => {
                return {
                    payoutType: payout.payoutType === '1' ? 'Uniform' : 'Unknown',
                    totalRewards: downscale(payout.totalRewards),
                    fromBlock: Number(payout.fromBlock),
                    durationBlocks: Number(payout.durationBlocks),
                };
            });
        });
    }
    initZeroRewardPayout(maxSupply, payoutType, fromBlock, toBlockExclusive, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const sender = this.keyProvider.currentAccount();
            const data = this.rewardPool.methods
                .initZeroRewardPayout(maxSupply, payoutType, fromBlock, toBlockExclusive, upscale(amount).toString(10))
                .encodeABI();
            return this.keyProvider.sendTransactionAsync(sender, this.rewardPool.options.address, {
                data: data,
                estimate: true,
            });
        });
    }
    depositRewardPayout(payoutType, fromBlock, toBlockExclusive, amount) {
        return __awaiter(this, void 0, void 0, function* () {
            const sender = this.keyProvider.currentAccount();
            const data = this.rewardPool.methods
                .depositRewardPayout(payoutType, fromBlock, toBlockExclusive, upscale(amount).toString(10))
                .encodeABI();
            return this.keyProvider.sendTransactionAsync(sender, this.rewardPool.options.address, {
                data: data,
                estimate: true,
            });
        });
    }
    isClaimUsed(claimId) {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.rewardPool.methods
                .isClaimUsed(claimId)
                .call();
            return Boolean(result);
        });
    }
    claimTokensFor(claimId, scaledAmount, claimBeforeBlock, account, signature) {
        return __awaiter(this, void 0, void 0, function* () {
            const makePrefixed = (val) => {
                if (!val.startsWith('0x'))
                    return `0x${val}`;
                return val;
            };
            const sender = this.keyProvider.currentAccount();
            const data = this.rewardPool.methods
                .claimTokensFor(makePrefixed(claimId), scaledAmount.toString(10), claimBeforeBlock, account, makePrefixed(signature))
                .encodeABI();
            return this.keyProvider.sendTransactionAsync(sender, this.rewardPool.options.address, {
                data: data,
                estimate: true,
            });
        });
    }
    claimableRewardsOf(account) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!account)
                account = this.keyProvider.currentAccount();
            const rawRewardPayouts = yield this.rewardPool.methods
                .claimableRewardsOf(account)
                .call();
            return downscale(rawRewardPayouts);
        });
    }
    getRewardPoolSymbol() {
        return __awaiter(this, void 0, void 0, function* () {
            return this.rewardPool.methods.symbol().call();
        });
    }
    getStakingTokenSymbol() {
        return __awaiter(this, void 0, void 0, function* () {
            const stakingTokenAddress = yield this.rewardPool.methods
                .getStakingToken()
                .call();
            const rewardTokenContract = this.keyProvider.createContract(RewardPool_json_1.default, stakingTokenAddress);
            return rewardTokenContract.methods
                .symbol()
                .call();
        });
    }
    isTokenClaim() {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.rewardPool.methods
                .isTokenClaim()
                .call();
            return Boolean(result);
        });
    }
    isParachainClaim() {
        return __awaiter(this, void 0, void 0, function* () {
            const result = yield this.rewardPool.methods
                .isParachainClaim()
                .call();
            return Boolean(result);
        });
    }
    claimTokenRewards() {
        return __awaiter(this, void 0, void 0, function* () {
            const sender = this.keyProvider.currentAccount();
            const data = this.rewardPool.methods
                .claimTokenRewards()
                .encodeABI();
            return this.keyProvider.sendTransactionAsync(sender, this.rewardPool.options.address, {
                data: data,
                estimate: true,
            });
        });
    }
    claimParachainRewards(recipientBase58) {
        return __awaiter(this, void 0, void 0, function* () {
            const recipientHex = bs58_1.default.decode(recipientBase58).toString('hex');
            const sender = this.keyProvider.currentAccount();
            const data = this.rewardPool.methods
                .claimParachainRewards(recipientHex)
                .encodeABI();
            return this.keyProvider.sendTransactionAsync(sender, this.rewardPool.options.address, {
                data: data,
                estimate: true,
            });
        });
    }
}
exports.ContractManager = ContractManager;
const upscale = (value, decimals = 18) => {
    return new bignumber_js_1.default(value).multipliedBy(Math.pow(10, decimals));
};
const downscale = (value, decimals = 18) => {
    return new bignumber_js_1.default(value).dividedBy(Math.pow(10, decimals));
};
//# sourceMappingURL=contract.js.map